import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Container,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Switch,
  Text,
  Textarea,
  useColorModeValue,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { Paperclip } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FiArrowLeft, FiTrash2, FiX } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { LoadingState } from '../../../global/components'
import { useTags } from '../../../hooks/agents/useTags'
import { AgentTracingMode } from '../../../types/agentTypes'
import { useLanguage } from '../../../hooks/utility/useLanguage'
import { IconSelector } from '../../selectors/IconSelector/IconSelector'
import ExampleQuestionsEditor from './ExampleQuestionsEditor'

export interface FormData {
  name: string
  description: string
  tags: string[]
  system_prompt: string
  profile_picture_url: string
  example_questions: string[]
  // Whether the agent may search documents uploaded in the conversation.
  fileSearchEnabled: boolean
  // Phoenix tracing: always / never / only when a traced tool is invoked.
  tracing_mode: AgentTracingMode
}

export interface AgentFormProps {
  mode: 'create' | 'edit'
  initialData?: Partial<FormData>
  isLoading?: boolean
  onSubmit: (data: FormData) => Promise<void>
  onDelete?: () => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  isDeleting?: boolean
  title: string
  subtitle: string
  submitButtonText: string
}

const AgentForm = ({
  mode,
  initialData = {},
  isLoading = false,
  onSubmit,
  onDelete,
  onCancel,
  isSubmitting = false,
  isDeleting = false,
  title,
  subtitle,
  submitButtonText,
}: AgentFormProps) => {
  const navigate = useNavigate()
  const { t } = useLanguage()

  // Color mode values
  const formBg = useColorModeValue('gray.50', 'gray.900')
  const inputBg = useColorModeValue('white', 'gray.700')
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const [customTag, setCustomTag] = useState('')
  const cancelRef = useRef<HTMLButtonElement>(null)

  // Hooks
  const { tags: availableTags, createTag } = useTags()

  // React Hook Form
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      tags: [],
      system_prompt: '',
      profile_picture_url: '',
      example_questions: [],
      fileSearchEnabled: true,
      tracing_mode: 'tool_based',
    },
  })

  const watchedTags = watch('tags')
  const watchedExampleQuestions = watch('example_questions')


  // Reset form when initial data changes (only for edit mode with actual data)
  useEffect(() => {
    if (mode === 'edit' && initialData && Object.keys(initialData).length > 0) {
      reset({
        name: initialData.name || '',
        description: initialData.description || '',
        tags: initialData.tags || [],
        system_prompt: initialData.system_prompt || '',
        profile_picture_url: initialData.profile_picture_url || '',
        example_questions: initialData.example_questions || [],
        fileSearchEnabled: initialData.fileSearchEnabled ?? true,
        tracing_mode: initialData.tracing_mode || 'tool_based',
      })
    }
  }, [mode, initialData, reset])

  // When adding a custom tag, create it in the backend if it doesn't exist, then add to form state
  const handleAddCustomTag = async () => {
    const trimmed = customTag.trim()
    if (!trimmed || watchedTags.includes(trimmed)) return
    // Check if tag already exists in availableTags
    const exists = availableTags.some((t: string) => t === trimmed)
    if (!exists) {
      try {
        await createTag({ name: trimmed })
      } catch (e) {
        // Optionally show error (e.g., toast)
        return
      }
    }
    setValue('tags', [...watchedTags, trimmed], { shouldDirty: true })
    setCustomTag('')
  }

  // Remove by tag name
  const handleRemoveTag = (tagToRemove: string) => {
    setValue(
      'tags',
      watchedTags.filter(tag => tag !== tagToRemove),
      { shouldDirty: true },
    )
  }

  // On submit, pass tag names as strings
  const handleFormSubmit = async (data: FormData) => {
    await onSubmit({ ...data, tags: watchedTags })
  }

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete()
    }
  }

  const handleBackClick = () => {
    if (isDirty) {
      onOpen()
    } else {
      if (onCancel) {
        onCancel()
      } else {
        navigate('/library')
      }
    }
  }

  const confirmBack = () => {
    onClose()
    if (onCancel) {
      onCancel()
    } else {
      navigate('/library')
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <LoadingState
        message={mode === 'edit' ? t('editAgent.loadingAgent') : t('common.loading')}
        fullHeight
        variant='spinner'
        size='lg'
      />
    )
  }

  return (
    <Box minH='100vh' bg={formBg}>
      <Container maxW='4xl' py={8} px={8}>
        {/* Header */}
        <HStack spacing={4} mb={8}>
          <IconButton
            aria-label='Go back'
            icon={<FiArrowLeft />}
            variant='ghost'
            size='lg'
            onClick={handleBackClick}
            color='app.text.primary'
          />
          <Box flex={1}>
            <Heading size='lg' color='primaryScheme.500'>
              {title}
            </Heading>
            <Text color='app.text.secondary' fontSize='sm' mt={1}>
              {subtitle}
            </Text>
          </Box>
          {mode === 'edit' && onDelete && (
            <Button leftIcon={<FiTrash2 />} colorScheme='red' variant='outline' onClick={onDeleteOpen} size='md'>
              {t('createAgent.deleteAssistant')}
            </Button>
          )}
        </HStack>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <VStack spacing={8} align='stretch'>
            {/* Single Form Card */}
            <Card>
              <CardBody p={8}>
                <VStack spacing={8} align='stretch'>
                  {/* Basic Information Section */}
                  <VStack spacing={6} align='stretch'>
                    <Heading size='md' color='app.text.primary' fontWeight='bold'>
                      {t('createAgent.basicInfo')}
                    </Heading>

                    <HStack spacing={8} align='start'>
                      {/* Icon */}
                      <Box>
                        <FormLabel color='app.text.primary' mb={3}>
                          {t('createAgent.icon')} *
                        </FormLabel>
                        <Controller
                          name='profile_picture_url'
                          control={control}
                          render={({ field }) => (
                            <IconSelector selectedIcon={field.value} onIconSelect={field.onChange} />
                          )}
                        />
                      </Box>

                      {/* Name */}
                      <FormControl isRequired isInvalid={!!errors.name} flex={1}>
                        <FormLabel color='app.text.primary'>{t('createAgent.name')}</FormLabel>
                        <Input
                          {...register('name', {
                            required: t('createAgent.fillAllRequiredFields'),
                            minLength: { value: 2, message: 'Name must be at least 2 characters' },
                          })}
                          placeholder={t('createAgent.namePlaceholder')}
                          bg={inputBg}
                          border='2px solid'
                          borderColor='gray.200'
                          _hover={{ borderColor: 'gray.300' }}
                          _focus={{
                            borderColor: 'primaryScheme.500',
                            boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
                          }}
                        />
                        <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                      </FormControl>
                    </HStack>

                    {/* Description */}
                    <FormControl isRequired isInvalid={!!errors.description}>
                      <FormLabel color='app.text.primary'>{t('createAgent.description')}</FormLabel>
                      <Textarea
                        {...register('description', {
                          required: t('createAgent.fillAllRequiredFields'),
                          minLength: { value: 10, message: 'Description must be at least 10 characters' },
                        })}
                        placeholder={t('createAgent.descriptionPlaceholder')}
                        rows={3}
                        bg={inputBg}
                        border='2px solid'
                        borderColor='gray.200'
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{
                          borderColor: 'primaryScheme.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
                        }}
                        resize='vertical'
                      />
                      <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
                    </FormControl>
                  </VStack>

                  <Divider />

                  {/* Tools Section */}
                  <VStack spacing={6} align='stretch'>
                    <Heading size='md' color='app.text.primary' fontWeight='bold'>
                      {t('createAgent.tools')}
                    </Heading>

                    <FormControl
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                      gap={4}
                      borderWidth='1px'
                      borderColor='gray.200'
                      borderRadius='md'
                      p={4}
                      bg={inputBg}
                    >
                      <HStack spacing={3} align='start'>
                        <Box color='primaryScheme.500' mt={1}>
                          <Paperclip size={18} />
                        </Box>
                        <Box>
                          <FormLabel htmlFor='fileSearchEnabled' mb={0} color='app.text.primary'>
                            {t('createAgent.toolsFileSearch')}
                          </FormLabel>
                        </Box>
                      </HStack>
                      <HStack spacing={2} flexShrink={0}>
                        <Text
                          fontSize='sm'
                          fontWeight='medium'
                          color={watch('fileSearchEnabled') ? 'primaryScheme.500' : 'app.text.secondary'}
                        >
                          {watch('fileSearchEnabled') ? t('common.enabled') : t('common.disabled')}
                        </Text>
                        <Switch
                          id='fileSearchEnabled'
                          size='lg'
                          colorScheme='primaryScheme'
                          isChecked={watch('fileSearchEnabled')}
                          onChange={e => setValue('fileSearchEnabled', e.target.checked, { shouldDirty: true })}
                        />
                      </HStack>
                    </FormControl>
                  </VStack>

                  <Divider />

                  {/* Tracing Section */}
                  <VStack spacing={4} align='stretch'>
                    <Box>
                      <Heading size='md' color='app.text.primary' fontWeight='bold'>
                        {t('createAgent.tracing')}
                      </Heading>
                      <Text color='app.text.secondary' fontSize='sm' mt={1}>
                        {t('createAgent.tracingHelp')}
                      </Text>
                    </Box>

                    <FormControl
                      borderWidth='1px'
                      borderColor='gray.200'
                      borderRadius='md'
                      p={4}
                      bg={inputBg}
                    >
                      <Controller
                        name='tracing_mode'
                        control={control}
                        render={({ field }) => (
                          <RadioGroup
                            value={field.value}
                            onChange={value => field.onChange(value as AgentTracingMode)}
                            colorScheme='primaryScheme'
                          >
                            <HStack spacing={8}>
                              <Radio value='always'>{t('createAgent.tracingAlways')}</Radio>
                              <Radio value='never'>{t('createAgent.tracingNever')}</Radio>
                              <Radio value='tool_based'>{t('createAgent.tracingToolBased')}</Radio>
                            </HStack>
                          </RadioGroup>
                        )}
                      />
                    </FormControl>
                  </VStack>

                  {/* Tags Section */}
                  <VStack spacing={4} align='stretch'>
                    <Box>
                      <HStack>
                        <Heading size='md' color='app.text.primary' fontWeight='bold'>
                          {t('createAgent.tags')}
                        </Heading>
                        <Badge variant='outline' colorScheme='gray' size='sm'>
                          {t('createAgent.optional')}
                        </Badge>
                      </HStack>
                      <Text color='app.text.secondary' fontSize='sm' mt={1}>
                        {t('createAgent.tagsHelp')}
                      </Text>
                    </Box>

                    {/* Add Custom Tag */}
                    <HStack spacing={4}>
                      <Input
                        placeholder={t('createAgent.addNewTag')}
                        value={customTag}
                        onChange={e => setCustomTag(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && handleAddCustomTag()}
                        bg={inputBg}
                        color='app.text.primary'
                        border='2px solid'
                        borderColor='gray.200'
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{
                          borderColor: 'primaryScheme.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
                        }}
                        size='sm'
                      />
                      <Button
                        onClick={handleAddCustomTag}
                        colorScheme='primaryScheme'
                        isDisabled={!customTag.trim()}
                        type='button'
                        size='sm'
                      >
                        {t('createAgent.add')}
                      </Button>
                    </HStack>

                    {/* Selected Tags */}
                    {watchedTags && watchedTags.length > 0 && (
                      <Box>
                        <Text color='app.text.primary' fontWeight='medium' mb={2} fontSize='sm'>
                          Selected tags
                        </Text>
                        <Wrap spacing={3}>
                          {watchedTags.map(tag => (
                            <WrapItem key={tag}>
                              <Badge
                                colorScheme='primaryScheme'
                                variant='solid'
                                px={2}
                                py={1}
                                borderRadius='full'
                                display='flex'
                                alignItems='center'
                                gap={1}
                                fontSize='xs'
                                textTransform='none'
                              >
                                {tag}
                                <IconButton
                                  aria-label={`Remove ${tag}`}
                                  icon={<FiX />}
                                  size='xs'
                                  variant='ghost'
                                  color='white'
                                  _hover={{ bg: 'rgba(255,255,255,0.2)' }}
                                  onClick={() => handleRemoveTag(tag)}
                                  type='button'
                                />
                              </Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Box>
                    )}
                  </VStack>

                  <Divider />

                  {/* Example Questions Section */}
                  <ExampleQuestionsEditor
                    questions={watchedExampleQuestions}
                    addExampleQuestion={(question: string) => {
                      setValue('example_questions', [...watchedExampleQuestions, question], { shouldDirty: true })
                    }}
                    removeExampleQuestion={(question: string) => {
                      setValue('example_questions', watchedExampleQuestions.filter(q => q !== question), { shouldDirty: true })
                    }}
                  />

                  <Divider />

                  {/* Instructions Section */}
                  <VStack spacing={4} align='stretch'>
                    <Box>
                      <Heading size='md' color='app.text.primary' fontWeight='bold'>
                        {t('createAgent.instructions')}
                      </Heading>
                      <Text color='app.text.secondary' fontSize='sm' mt={1}>
                        {t('createAgent.instructionsDescription')}
                      </Text>
                    </Box>

                    <FormControl isRequired isInvalid={!!errors.system_prompt}>
                      <FormLabel color='app.text.primary'>{t('createAgent.instructionsLabel')}</FormLabel>
                      <Textarea
                        {...register('system_prompt', {
                          required: t('createAgent.fillAllRequiredFields'),
                          minLength: { value: 20, message: t('createAgent.instructionsMinLength') },
                        })}
                        placeholder={t('createAgent.instructionsPlaceholder')}
                        rows={10}
                        bg={inputBg}
                        border='2px solid'
                        borderColor='gray.200'
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{
                          borderColor: 'primaryScheme.500',
                          boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
                        }}
                        resize='vertical'
                      />
                      <FormHelperText color='app.text.secondary'>
                        {t('createAgent.instructionsHelperText')}
                      </FormHelperText>
                      <FormErrorMessage>{errors.system_prompt?.message}</FormErrorMessage>
                    </FormControl>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Action Buttons */}
            <HStack justify='space-between' pt={2} spacing={4}>
              <Button variant='outline' onClick={handleBackClick} size='lg' type='button'>
                {t('common.cancel')}
              </Button>
              <Button
                colorScheme='primaryScheme'
                type='submit'
                isLoading={isSubmitting}
                loadingText='Saving...'
                size='lg'
                px={8}
              >
                {submitButtonText}
              </Button>
            </HStack>
          </VStack>
        </form>
      </Container>

      {/* Unsaved Changes Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('createAgent.unsavedChanges')}</ModalHeader>
          <ModalBody>
            <Text>{t('createAgent.unsavedChangesMessage')}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant='ghost' mr={3} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button colorScheme='red' onClick={confirmBack}>
              {t('common.back')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog (only in edit mode) */}
      {mode === 'edit' && onDelete && (
        <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose} isCentered>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                {t('createAgent.deleteAssistant')}
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to delete this agent? This action cannot be undone.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteClose}>
                  {t('common.cancel')}
                </Button>
                <Button
                  colorScheme='red'
                  onClick={handleDelete}
                  ml={3}
                  isLoading={isDeleting}
                  loadingText='Deleting...'
                >
                  {t('createAgent.deleteAssistant')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </Box>
  )
}

export default AgentForm
